'use client';

import moment, { Moment } from "moment"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import * as echarts from 'echarts'

import { categoricalColorSchemes } from "@nivo/colors"
import { useDarkMode } from "@/hooks/useDarkMode";
import { useIsMobile } from "@/hooks/isMobile";
import { Button } from "../ui/button";
import { TrimContext } from "@/contexts/trimContext";

export type ColorSchemeKey = keyof typeof categoricalColorSchemes;

export interface DatasetSource {
  source: (Date | number | string)[][];
  dimensions?: string[];
}

export interface DatasetSeriesConfig {
  name?: string;
  type?: 'line';
  color?: string;
}

export interface TooltipConfig {
  xAxisFormatter?: (value: Date) => string;
  seriesNameFormatter?: (name: string) => string;
  seriesValueFormatter?: (value: number) => string;
}

export interface LegendAction {
  name: string,
  icon: any,
  action: () => void
}

export interface DatasetAreaChartProps {
  // Required
  dataset: DatasetSource;

  // Optional configuration
  title?: string;
  description?: string;
  height?: string;
  showLegend?: boolean;
  
  // Y-axis configuration
  yAxisFormatter?: (value: number) => string;
  yAxisMax?: number;
  yAxisScale?: boolean;
  connectNulls?: boolean;
  smooth?: boolean;
  
  // Tooltip configuration
  tooltip?: TooltipConfig;
  tooltipTopN?: number;
  
  // Styling
  colorScheme?: ColorSchemeKey;
  stack?: boolean;
  stackOrder?: 'ascending' | 'descending' | 'insideOut' | 'none' | 'seriesDesc' | 'seriesAsc';
  symbol?: 'circle' | 'emptyCircle' | 'rect' | 'roundRect' | 'triangle' | 'diamond' | 'pin' | 'arrow' | 'none';
  
  // Series configuration (optional - will be auto-generated if not provided)
  series?: DatasetSeriesConfig[];

  trim?: Moment
  showTotal?: boolean

  legendActions?: LegendAction[]
}

const defaultProps: Partial<DatasetAreaChartProps> = {
  height: 'h-full',
  showLegend: true,
  yAxisScale: true,
  colorScheme: 'tableau10',
  stack: true,
  stackOrder: 'seriesDesc',
  symbol: 'none',
  connectNulls: false,
  smooth: false,
  legendActions: []
} as const;

const defaultValueFormatter: (n: number) => string = (n: number) => {
  if (n > 1) {
    return n.toString()
  } else {
    return n.toExponential()
  }
}

// Extract series names from dataset
const extractSeriesNames = (dataset: DatasetSource, trim: Date | null): string[] => {
  let notSeries = ['day', 'month', 'date', 'block_date', 'date_unix']

  const dimensionsWithinTrimmedData = trim?dataset.source
    .filter(s => s[0] >= trim)
    .reduce((acc, r) => 
      r.slice(1).map((v, i) => !!v || acc[i]),
      new Array((dataset.source[0]?.length - 1 || 0)).map(() => false)
    ):(dataset.dimensions||dataset.source[0]).map(_ => true)

  // Method 1: Use dimensions if provided
  if (dataset.dimensions) {
    return dataset.dimensions.slice(1).filter(k => !notSeries.includes(k)).filter((_, i) => dimensionsWithinTrimmedData[i]); // Skip first column (x-axis)
  }
  
  // Method 2: Use first row as header if it contains strings
  const firstRow = dataset.source[0];
  if (firstRow && firstRow.every(cell => typeof cell === 'string')) {
    return (firstRow.slice(1) as string[]).filter(k => !notSeries.includes(k)).filter((_, i) => dimensionsWithinTrimmedData[i]);
  }
  
  // Method 3: Auto-generate from column indices
  const columnCount = dataset.source[0]?.length - 1 || 0;
  return Array.from({ length: columnCount }, (_, i) => String(i + 1)).filter(k => !notSeries.includes(k));
};

export function DatasetAreaChart({
  dataset,
  title,
  description,
  height = defaultProps.height,
  showLegend = defaultProps.showLegend,
  yAxisFormatter,
  yAxisMax,
  yAxisScale = defaultProps.yAxisScale,
  tooltip,
  tooltipTopN,
  colorScheme = defaultProps.colorScheme,
  stack = defaultProps.stack,
  stackOrder = defaultProps.stackOrder,
  symbol = defaultProps.symbol,
  connectNulls = defaultProps.connectNulls,
  smooth = defaultProps.smooth,
  series: seriesConfig,
  showTotal,
  legendActions = defaultProps.legendActions
}: DatasetAreaChartProps) {
  const [hovered, setHovered] = useState<string | undefined>(undefined)
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set())
  const trim = useContext(TrimContext)
  const [chartInstance, setChartInstance] = useState<echarts.ECharts>()
  const chartRef = useRef<HTMLDivElement>(null)
  const isDark = useDarkMode()
  const isMobile = useIsMobile()
 
  const colors = categoricalColorSchemes[colorScheme || 'tableau10'];
 
  // Extract series names from dataset
  const seriesNames = useMemo(() => {
    return extractSeriesNames(dataset, trim);
  }, [dataset, trim]);

  const defaultTooltipFormatter = (
    params: any[]
  ) => {
    const date = params[0].axisValue;
    const xAxisFormatter = tooltip?.xAxisFormatter || ((v: Date) => moment.utc(v).format('YYYY-MM-DD'))
    let resultGrid = `<div class='grid grid-cols-2 auto-cols-fr text-xs'>`
    resultGrid += `<div class='col-span-2'><span>${xAxisFormatter(date)}</span></div>`

    type Entry = { value: number; name: string; marker: string; isHovered: boolean }
    let entries: Entry[] = params
      .map(param => {
        const value = param.value[param.seriesIndex + 1] as number
        return {
          value,
          name: param.seriesName,
          marker: param.marker,
          isHovered: param.seriesName === hovered,
        }
      })
      .filter(e => !(Number.isNaN(e.value) || e.value === 0 || !e.value))

    if (tooltipTopN !== undefined && entries.length > tooltipTopN) {
      entries.sort((a, b) => b.value - a.value)
      const otherSum = entries.slice(tooltipTopN).reduce((acc, e) => acc + e.value, 0)
      entries = [
        ...entries.slice(0, tooltipTopN),
        {
          value: otherSum,
          name: 'Other',
          marker: '<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background:#888;"></span>',
          isHovered: false,
        },
      ]
    }

    for (let e of entries) {
      const classes = e.isHovered ? 'font-bold text-sm' : ''
      const formattedName = tooltip?.seriesNameFormatter
        ? tooltip.seriesNameFormatter(e.name)
        : e.name;
      const formattedValue = tooltip?.seriesValueFormatter
        ? tooltip.seriesValueFormatter(e.value)
        : defaultValueFormatter(e.value);

      resultGrid += `<div class="${classes}">${e.marker} ${formattedName}</div><div class='text-right ${classes}''>${formattedValue}</div>`
    }
    if (showTotal) {
        const to_add = params[0].dimensionNames.slice(1).map((d: string, idx: number) => {
          const name = seriesNames[idx]
          if (!seriesNames.includes(name)) return 0;
          if (hiddenSeries.has(name)) return 0;
          else return params[0].value.slice(1)[idx]
        })
        const total = to_add.reduce((acc: number, v?: string) => acc + (Number(v) || 0), 0) as number
        const formattedTotal = tooltip?.seriesValueFormatter
            ? tooltip.seriesValueFormatter(total)
            : defaultValueFormatter(total);
        resultGrid += `<div class="font-bold">Total</div><div class='font-bold text-right'>${formattedTotal}</div>`
    }
    
    return resultGrid;
  };

  useEffect(() => {
    const element = chartRef.current;
    if (!element) return;

    // Ensure element has dimensions before initializing
    const initChart = () => {

      const _chartInstance = echarts.init(element);
      setChartInstance(_chartInstance);

      // Handle window resize
      const handleResize = () => {
        _chartInstance.resize();
      };

      // Handle container resize using ResizeObserver
      const resizeObserver = new ResizeObserver(() => {
        _chartInstance.resize();
      });
      resizeObserver.observe(element);

      // Handle series hover
      const handleMouseOver = (params: any) => {
        if (params.componentType === 'series') {
          setHovered(params.seriesName);
        }
      };

      const handleMouseOut = () => {
        setHovered(undefined);
      };

      window.addEventListener('resize', handleResize);
      _chartInstance.on('mouseover', handleMouseOver);
      _chartInstance.on('globalout', handleMouseOut);

      return () => {
        window.removeEventListener('resize', handleResize);
        resizeObserver.disconnect();
        _chartInstance.off('mouseover', handleMouseOver);
        _chartInstance.off('globalout', handleMouseOut);
        _chartInstance.dispose();
      };
    };

    // Start initialization
    const cleanup = initChart();

    return () => {
      cleanup?.();
    };
  }, [])

  useEffect(() => {
    if (!seriesNames || seriesNames.length === 0) return;
    const textColor = isDark?"#ccc":"#222"

    const yAxisConfig: any = {
      type: 'value',
      scale: trim?true:yAxisScale,
      axisLabel: {
        formatter: yAxisFormatter ? (n: any) => yAxisFormatter(n) : undefined,
        color: textColor
      },
      axisLine: {
        lineStyle: {
          color: textColor
        }
      },
      axisTick: {
        lineStyle: {
          color: textColor
        }
      },
      max: () => {
        return Math.max(...(dataset
          .source
          .filter(r => trim?r[0] >= trim:true)
          .map(r => r.slice(1))
          .map(r => r.map((d, i) => hiddenSeries.has(seriesNames[i])?0:d))
          .map(r => stack?[r.reduce((a, i) => (a as number) + (i as number), 0)]:r)
          .flat() as number[]))
      },
    };

    if (yAxisMax !== undefined) {
      yAxisConfig.max = yAxisMax;
    }

    const breaks = trim?[{
      start: 0,
      end: trim
    }]:undefined

    const dataZoom = [{
      type: 'slider',
      show: false,
      xAxisIndex: [0],
      startValue: trim??undefined,
      filterMode: 'none'
    }]

    // Build series configuration
    const series = seriesNames.map((name, idx) => {
      const config = seriesConfig?.[idx] || {};
      return {
        type: 'line',
        name: config.name || name,
        areaStyle: {
          opacity: (hovered ? (hovered === (config.name || name) ? 1 : 0.5) : 1)
        },
        lineStyle: {
          opacity: (hovered ? (hovered === (config.name || name) ? 1 : 0.5) : 1)
        },
        data: hiddenSeries.has(name)?[]:undefined,
        connectNulls: connectNulls,
        smooth: smooth,
        stack: stack ? 'total' : undefined,
        stackOrder: stack ? stackOrder : undefined,
        symbol: symbol,
        color: config.color || colors[idx % colors.length],
        triggerLineEvent: true,
        triggerAreaEvent: true,
        encode: {
          x: 0, // First column is x-axis
          y: idx + 1 // Series data is in column idx + 1
        }
      };
    });

    chartInstance?.setOption({
      darkMode: isDark,
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '3%',
        containLabel: true
      },
      dataset: {
        source: dataset.source
      },
      xAxis: {
        type: 'time',
        name: 'Date',
        //breaks,
        axisLabel: {
          color: textColor
        },
        axisLine: {
          lineStyle: {color: textColor}
        },
        axisTick: {
          lineStyle: {color: textColor}
        }
      },
      dataZoom,
      yAxis: yAxisConfig,
      series,
      tooltip: {
        trigger: 'axis',
        formatter: defaultTooltipFormatter,
        className: 'text-fg bg-background xs:text-xs',
        backgroundColor: '',
        textStyle: {
          color: '',
        },
      },
      legend: {
        show: false
      }
    })
  }, [isDark, chartInstance, dataset, hovered, title, yAxisFormatter, yAxisMax, yAxisScale, tooltip, tooltipTopN, stack, stackOrder, symbol, colors, trim, seriesNames, seriesConfig, hiddenSeries])

  const handleLegendClick = (name: string) => {
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    setHiddenSeries(prev => {
      // If all series are hidden, show all; otherwise hide all
      if (prev.size === seriesNames.length) {
        return new Set();
      } else {
        return new Set(seriesNames);
      }
    });
  };

  const textColor = isDark?"#ccc":"#222"

  return (
    <div className='flex flex-col h-full lg:flex-row'>
      <div className='flex flex-col grow'> 
        { /* title */ }
        <div className="flex flex-row">
          <span className="block text-2xl lg:ml-8 ml-4 grow">{title}</span>
          <span className="block text-xs place-self-end">{description}</span>
        </div>
        { /* chart */}
        <div className='w-full h-full' ref={chartRef} />
      </div>
      {/* legend */}
      {showLegend && (
        <div className='flex flex-col'>
        
          <div className={`flex hover:bg-primary/10 bg-muted rounded ${isMobile ? 'flex-row w-full' : 'flex-col w-[10vw] h-full ml-4'} gap-2 p-2 overflow-scroll`}>
          <button
            onClick={handleToggleAll}
            className={`flex items-center gap-2 px-2 py-1 rounded text-sm transition-opacity font-medium ${
              hiddenSeries.size === seriesNames.length ? 'opacity-100' : 'opacity-100'
            }`}
            style={{ color: textColor }}
          >
            <span className="truncate">
              {hiddenSeries.size === seriesNames.length ? 'Show All' : 'Hide All'}
            </span>
          </button>
          {legendActions?.map(action => <button
            onClick={action.action}
            className={`flex items-center gap-2 px-2 py-1 rounded text-sm font-medium`}
            style={{ color: textColor }}
            key={action.name}
          >
            <action.icon size='14' />
            <span className="truncate">
              {action.name}
            </span>
          </button>)}
            {seriesNames.map((name, idx) => {
            const config = seriesConfig?.[idx] || {};
            const displayName = config.name || name;
            const isHidden = hiddenSeries.has(displayName);
            return (
              <button
                key={displayName}
                onClick={() => handleLegendClick(displayName)}
                onMouseEnter={() => setHovered(displayName)}
                onMouseLeave={() => setHovered(undefined)}
                className={`flex items-center gap-2 px-2 py-1 rounded text-sm transition-opacity ${
                  isHidden ? 'opacity-40' : 'opacity-100'
                } ${hovered === displayName ? 'bg-primary/10' : ''}`}
                style={{ color: textColor }}
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: config.color || colors[idx % colors.length] }}
                />
                <span className="truncate">{displayName.trim()}</span>
              </button>
            );
          })}
          </div>
      </div>)}

    </div>)
}
